import { useEffect, useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconArrowRight } from '@tabler/icons-react';
import { useReturnExperienceContext } from 'helpers/ReturnExperienceContext';

import { ExperienceType } from '../../helpers/returnExperience';
import { getOfferDashboardUrl, getSellerFlowUrl } from './returnExperienceUrls';

type CTAButtonProps = {
  experience: ExperienceType;
  loading?: boolean;
  onClick?: () => void;
  url?: string;
  actionText: string;
  isDirectMail?: boolean;
};

const AltCTAButton = (props: CTAButtonProps): JSX.Element => {
  return (
    <Button
      aria-label={props.actionText}
      analyticsName={`cosmos-return-experience-${ExperienceType[props.experience]}-icon-button`}
      trackImpression
      variant="secondary"
      size="sm"
      onClick={() => props.url && window.location.assign(props.url)}
      className="h-6 w-6 rounded-full border-transparent bg-transparent p-0 not-disabled:hover:border-transparent not-disabled:hover:bg-transparent"
    >
      <IconArrowRight size={20} aria-hidden="true" />
    </Button>
  );
};

const getActiveOfferExpiryText = (daysToExpire = 10) => {
  let expiryText = 'expires in 2 days';
  if (daysToExpire < 1) {
    expiryText = 'expires today';
  } else if (daysToExpire < 2) {
    expiryText = 'expires in 1 day';
  } else if (daysToExpire < 3) {
    expiryText = 'expires in 2 days';
  } else if (daysToExpire === 3) {
    expiryText = 'expires in 3 days';
  }
  return expiryText;
};

type HeadlineProps = {
  experience: ExperienceType;
  currentOfferPriceCents?: number;
  previousOfferPriceCents?: number;
  daysToExpire?: number;
};

const Headline = (props: HeadlineProps): JSX.Element | null => {
  switch (props.experience) {
    case ExperienceType.direct_mailer:
      return <>Get started on your offer</>;
    case ExperienceType.offer_expired:
      return <>Get your updated offer today</>;
    case ExperienceType.incomplete_flow:
      return <>Finish your offer today</>;
    case ExperienceType.offer_increased:
      return <>Your offer increased</>;
    case ExperienceType.offer_expiring:
    case ExperienceType.offer_decreased:
    case ExperienceType.active_offer:
      return <>Your offer {getActiveOfferExpiryText(props.daysToExpire)}</>;
    default:
      return null;
  }
};

type ExperienceCTAButtonProps = {
  experience: ExperienceType;
  sellerInputUUID?: string;
  customerUUID?: string;
  addressToken?: string;
  sellerFlowLastCompletedSlug?: string;
};

const ExperienceCTAButton = (props: ExperienceCTAButtonProps): JSX.Element | null => {
  const offerDashUrl = getOfferDashboardUrl(props.addressToken);

  switch (props.experience) {
    case ExperienceType.offer_expired: {
      const baseUrl = offerDashUrl;
      const params = new URLSearchParams({
        refresh_offer: 'true',
        refresh_source: 'homepage_experience',
      }).toString();
      const refreshUrl = `${baseUrl}?${params}`;
      return (
        <AltCTAButton
          actionText="Refresh your offer"
          url={refreshUrl}
          experience={props.experience}
        />
      );
    }
    case ExperienceType.incomplete_flow: {
      const sellerFlowUrl = getSellerFlowUrl(
        props.sellerInputUUID,
        props.sellerFlowLastCompletedSlug,
      );
      return (
        <AltCTAButton
          actionText="Resume your offer"
          url={sellerFlowUrl}
          experience={props.experience}
        />
      );
    }
    case ExperienceType.offer_increased:
    case ExperienceType.offer_expiring:
    case ExperienceType.offer_decreased:
    case ExperienceType.active_offer:
      return (
        <AltCTAButton
          actionText="View your offer"
          url={offerDashUrl}
          experience={props.experience}
        />
      );
    default:
      return null;
  }
};

export const ReturnExperienceHero = () => {
  const { returnExperienceData } = useReturnExperienceContext();

  // Slide + fade in on the frame after mount (replaces the Bricks
  // `contentfulArchAnimation` enter animation: y -30 → 0, opacity 0 → 1).
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, []);

  if (!returnExperienceData || returnExperienceData?.experienceType === null) {
    return null;
  }

  return (
    <Row
      className={cn(
        'relative -top-7 items-center justify-between rounded-b-3xl bg-od-bg-secondary p-4 pt-11',
        'transition-[opacity,transform] duration-1000 ease-[cubic-bezier(0.55,0,0.15,1)]',
        entered ? 'translate-y-0 opacity-100' : '-translate-y-8 opacity-0',
      )}
      analyticsName="cosmos-return-experience-address-entry-slide-in"
    >
      <Col className="gap-1">
        <Text as="span" className="text-label-sm text-od-content-primary lg:text-label-md">
          <Headline
            experience={returnExperienceData.experienceType}
            currentOfferPriceCents={returnExperienceData.currentOfferHeadlinePriceCents}
            previousOfferPriceCents={returnExperienceData.previousOfferHeadlinePriceCents}
            daysToExpire={returnExperienceData.offerExpiresInDays}
          />
        </Text>
        <Text as="span" className="text-body-sm text-od-content-secondary">
          {returnExperienceData.address}
        </Text>
      </Col>
      <ExperienceCTAButton
        experience={returnExperienceData.experienceType}
        sellerInputUUID={returnExperienceData.sellerInputUUID}
        customerUUID={returnExperienceData.customerUUID}
        addressToken={returnExperienceData.addressToken}
        sellerFlowLastCompletedSlug={returnExperienceData.sellerFlowLastCompletedSlug}
      />
    </Row>
  );
};
